import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError } from "../errors";
import schemaValidators from "./schema-validators";

export default class CourseClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	getCourseInfoFromId = async (id: number) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/${id}`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.courseInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getCourseInfoFromLmsCourseId = async ({
		lmsCourseId,
		instituteId,
	}: {
		lmsCourseId: string;
		instituteId?: number;
	}) => {
		try {
			const url = new URL(`${this.config.verityOrigin}/api/verity/course/lms-course/${lmsCourseId}`);
			if (instituteId) {
				url.searchParams.set("instituteId", instituteId.toString());
			}
			const response = await super.fetch(url, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.courseInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getPolicyPdfFromInstituteId = async({
		instituteId,
	}: {
		instituteId: number;
	}): Promise<string | undefined> => {
		try {
			const url = new URL(
				`${this.config.verityOrigin}/api/verity/agreement/${instituteId}/url`
			);
			
			const response = await super.fetch(url, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.agreementUrl.validate(obj)) {
					return obj.url;
				}
			} else {
				return undefined;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	}

	getCourseIdFromUuid = async (uuid: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/uuid:${uuid}`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.courseId.validate(obj)) {
					return obj.id;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getInstituteCoursesInfo = async (instituteId: number) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/institute/${instituteId}`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.courseInfoList.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAccessControlCourse = async (courseUuid: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/course/${courseUuid}`);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.accessControlCourseInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getCourseCountForPrefix = async (instituteUuid: string, namePrefix: string) => {
		try {
			const searchParams = new URLSearchParams();
			searchParams.set("instituteId", instituteUuid);
			searchParams.set("name", namePrefix);
			searchParams.set("matchNameMode", "STARTS_WITH");
			searchParams.set("ignoreCase", "0");
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/course?${searchParams.toString()}`,
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.courseCount.validate(obj)) {
					return obj.totalElements;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
